<template>
  <div>
    <b-row>
      <!-- ==============================  FILTERS_SECTION START ============================== -->
      <b-col class="col-lg-2 col-md-3 col-12">
        <AppSearchInput
          :value="filter.search"
          :label="$t('search') + ':'"
          :placeholder="$t('search') + '...'"
          @callback="search"
        />
      </b-col>

      
      <b-col
        class="col-lg-2 col-md-3 col-12"
        v-if="checkButtonPermission(['admin'])"
      >
        <b-form-group :label="$t('filter_by_author') + ':'">
          <v-select
            v-model="filter.author"
            class="bg-white rounded"
            :get-option-label="(el) => {return el.first_name + ' ' + el.last_name;}"
            label="name"
            :options="GET_APPLICATION_AUTHOR.results"
            :reduce="(p) => p.id"
            @input="onFiltered"
            @search="onAuthorSearch"
          >
            <li slot="list-footer" class="pagination">
              <button :disabled="!GET_APPLICATION_AUTHOR.previous" @click="previousAuthor">
                {{ $t("prev") }}
              </button>
              <button :disabled="!GET_APPLICATION_AUTHOR.next" @click="nextAuthor">
                {{ $t("next") }}
              </button>
            </li>
          </v-select>
        </b-form-group>
      </b-col>

      <b-col class="col-lg-2 col-md-3 col-12">
        <b-form-group :label="$t('filter_by_status') + ':'">
          <v-select
            v-model="filter.status"
            class="bg-white rounded"
            label="name"
            :options="GET_APPLICATION_STATUS.results"
            :reduce="(p) => p.id"
            @input="onFiltered"
          />
        </b-form-group>
      </b-col>

      <b-col
        class="col-lg-2 col-md-3 col-12"
        v-if="checkButtonPermission(['chief_manager', 'admin'])"
      >
        <b-form-group :label="$t('filter_by_warehouses') + ':'">
          <v-select
            v-model="filter.storage"
            class="bg-white rounded"
            label="title"
            :options="GET_APPLICATION_TYPE"
            :reduce="(p) => p.id"
            @input="onFiltered"
          />
        </b-form-group>
      </b-col>

      <b-col class="col-lg-2 col-md-3 col-12">
        <b-form-group :label="$t('dateFromTo') + ':'">
          <VueDataPicker
            v-model="dateFilter"
            placeholder=""
            :other-props="{ 'is-range': true }"
            @input="datePicker"
          />
        </b-form-group>
      </b-col>

      <b-col class="col-lg-2 col-md-3 col-12 d-flex align-items-center">
        <b-form-checkbox
          v-model="filter.show_archive"
          name="check-button"
          @input="onFiltered"
          switch
          inline
        >
          {{$t('archive')}}
        </b-form-checkbox>
      </b-col>


      <b-col class="col-lg-2 col-md-3 col-12 d-flex align-items-center">
        <b-form-checkbox
          v-model="filter.show_purchase"
          name="check-button"
          @input="onFiltered"
          switch
          inline
        >
          {{$t('sidebar.for_purchase')}}
        </b-form-checkbox>
      </b-col>

      <b-col
        class="col-lg-2 col-md-3 col-12 text-right mt-75"
        v-if="checkButtonPermission(['builder', 'admin'])"
      >
        <b-button
          v-b-modal.add-app
          class="w-100"
          variant="primary"
          id="add-material-modal-open"
          @click="$router.push({ name: 'add-material' })"
        >
          <feather-icon icon="PlusIcon" size="16" />
          {{ $t("add") }}
        </b-button>
      </b-col>
      <!-- ==============================  FILTERS_SECTION END ============================== -->

      <b-col class="col-md-12 col-12 mt-2">
        <!-- ==============================  TABLE_SECTION START ============================== -->
        <!-- <b-card > -->
        <b-table
          v-if="!isLoading"
          id="user-table"
          class="px-1 rounded bg-light-secondary dark"
          :fields="fields"
          :items="GET_APPLICATIONS_MATERIAL.results"
          show-empty
          :tbody-tr-class="['tableRowClass']"
          small
          responsive
          :empty-text="$t('empty_text')"
        >
          <template #head()="props">
            <span>{{ $t(props.label) }}</span>
          </template>

          <template #cell(from)="props">
            <h6
              class="font-weight-bolder"
              v-if="props && props.item && props.item.author"
            >
              {{ props.item.author.rc_object }}
            </h6>
            <small v-if="props && props.item && props.item.author"
              >{{ props.item.author.storage }}
              <feather-icon
                v-if="props.item.device.type === 'desktop'"
                icon="MonitorIcon"
              />
              <feather-icon v-else icon="SmartphoneIcon" />
            </small>
          </template>

          <template #cell(storage)="props">
            <span v-if="props && props.item && props.item.storage">{{
              props.item.storage.title
            }}</span>
          </template>

          <template #cell(goods)="props">
            <div v-if="props && props.item && props.item.goods">
              <b-badge
                class="mb-25 ml-1"
                v-for="(item, index) in props.item.goods"
                :key="index"
              >
                {{ item && item.good && item.good.value ? item.good.value : '' }}
                - {{ $_formatPriceMixin(item.start_count) }}
              </b-badge>
            </div>
          </template>

          <template #cell(status)="props">
            <b-badge
              v-if="props && props.item && props.item.status"
              :variant="props.item.status.color"
            >
              <span>{{ props.item.status.name }}</span>
            </b-badge>
            <feather-icon
              v-if="props && props.item && props.item.status"
              v-b-tooltip.focus="{ variant: props.item.status.color }"
              :title="props.item.status.description"
              class="ml-25"
              icon="InfoIcon"
              size="24"
            />
          </template>

          <template #cell(created_at)="props">
            <h6 class="font-weight-bolder">
              {{ $dayjs(props.value).format("DD.MM.YYYY") }} <br />
              {{ $dayjs(props.value).format("HH:mm") }}
            </h6>
          </template>

          <template #cell(actions)="props">
            <div class="d-flex align-items-center justify-content-center">
              <b-button
                v-if="props.item"
                v-b-tooltip.hover.v-primary
                :title="$t('watch')"
                size="sm"
                class="mt-50 mb-50"
                variant="primary"
                @click="$router.push(`/applications/material/view/${props.item.id}`)"
              >
                <feather-icon icon="EyeIcon" size="15" />
              </b-button>

              <b-button
                v-if="
                  props &&
                  props.item &&
                  props.item.status &&
                  props.item.status.is_editable
                "
                v-b-tooltip.hover.v-warning
                :title="$t('edit')"
                size="sm"
                variant="warning"
                class="ml-50 mt-50 mb-50"
                @click="$router.push(`/applications/material/${props.item.id}`)"
              >
                <feather-icon icon="EditIcon" size="15" />
              </b-button>
              <b-button
                v-if="props.item.status.is_editable && !(currentUserRole.includes('foreman'))"
                v-b-tooltip.hover.v-danger
                :title="$t('delete')"
                size="sm"
                variant="danger"
                class="ml-50 mt-50 mb-50"
                @click="deleteMaterial(props)"
              >
                <feather-icon icon="TrashIcon" size="15" />
              </b-button>
            </div>
          </template>
        </b-table>
        <!-- </b-card> -->

        <b-card v-else>
          <Loader />
        </b-card>
        <!-- ==============================  TABLE_SECTION END ============================== -->

      </b-col>




      <!-- ==============================  PAGINATION_SECTION START ============================== -->
      <b-col>
        <Pagination
          class="w-100"
          :params="filter"
          :rows="GET_APPLICATIONS_MATERIAL.count"
          @refresh="refresh"
          :page="filter.page"
          :page_size="filter.page_size"
        />
      </b-col>
      <!-- ==============================  PAGINATION_SECTION END ============================== -->
    
    
    
    
    </b-row>
  </div>
</template>

<script>
import {
  BCol,
  BRow,
  BButton,
  BTable,
  BCard,
  BFormGroup,
  BBadge,
  VBTooltip,
  BFormCheckbox,
} from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import vSelect from "vue-select";
import AppSearchInput from "@/components/AppSearchInput.vue";
import VueDataPicker from "@/components/VueDatePicker.vue";
import Pagination from "@/components/Pagination.vue";
import areYouSure from "@/mixins/areYouSure";
import check from "@/mixins/check";
import priceFormatMixin from "@/mixins/priceFormatMixin";
import loader from "@/mixins/loader";
import Loader from "@/components/Loader.vue";
import useJwt from "@/auth/jwt/useJwt";
import addQueryToCurrentPage from "@/mixins/addQueryToCurrentPage";

export default {
  components: {
    BCol,
    BRow,
    BButton,
    BFormGroup,
    vSelect,
    AppSearchInput,
    VueDataPicker,
    BTable,
    BBadge,
    Loader,
    BCard,
    BFormCheckbox,
    Pagination,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  mixins: [areYouSure, check, priceFormatMixin, loader, addQueryToCurrentPage],
  data() {
    return {
      isLoading: false,
      // ===============  PRIMARY_DATA_SECTION START ===============
      fields: [
        {
          label: "№",
          key: "id",
          thClass: "tableHeadClass",
        },
        {
          label: "application_from",
          key: "from",
          thClass: "tableHeadClass",
        },
        {
          label: "destination_warehouse",
          key: "storage",
          thClass: "tableHeadClass",
        },
        {
          label: "commodity",
          key: "goods",
          thClass: "tableHeadClass",
        },
        {
          label: "general.created_at",
          key: "created_at",
          thClass: "tableHeadClass",
        },
        {
          label: "status",
          key: "status",
          thClass: "tableHeadClass",
        },
        {
          label: "actions",
          key: "actions",
          thClass: "tableHeadClass",
        },
      ],
      filter: {
        author: "",
        status: "",
        search: "",
        storage: "",
        show_archive: 0,
        show_purchase: 0,
        full: 1, // Used to get more info about product
        page_size: 10,
        page: 1,
        // hide_tests: true // Used to hide tests
      },
      authorPage: 1,
      dateFilter: {
        start_date: "",
        end_date: "",
      },
      currentUserRole: '',
      // ===============  PRIMARY_DATA_SECTION END ===============
    };
  },
  computed: {
    ...mapGetters("application", [
      "GET_APPLICATIONS_MATERIAL",
      "GET_APPLICATION_STATUS",
      "GET_APPLICATION_AUTHOR",
      "GET_APPLICATION_TYPE",
      "GET_APPLICATIONS_MATERIAL_ID",
    ]),
  },
  async mounted() {
    const { role } = useJwt.getUserData();
    this.currentUserRole = role

    // await this.$_loaderStart();
    this.isLoading = true;




    // ===============  PRIMARY_REQUESTS_SECTION START ===============
    await this.FETCH_APPLICATION_AUTHOR({ role_unique_name: "builder", search: this.$route.query.author });
    await this.FETCH_APPLICATION_STATUS({ content_type_model: "requirements" });
    await this.FETCH_APPLICATION_TYPE({ is_object: 1, mini: true });



    await this.FETCH_APPLICATIONS_MATERIAL({
      page: this.$route.query.page ? this.$route.query.page : 1,
      page_size: this.$route.query.page_size ? this.$route.query.page_size : 10,

      author: this.$route.query.author ? this.$route.query.author : "",
      status: this.$route.query.status ? this.$route.query.status : "",
      search: this.$route.query.search ? this.$route.query.search : "",
      storage: this.$route.query.storage ? this.$route.query.storage : "",

      start_date: this.$route.query.start_date ? this.$route.query.start_date : "",
      end_date: this.$route.query.end_date ? this.$route.query.end_date : "",
      full: 1,
      // hide_tests: true
    });



    let status = this.GET_APPLICATION_STATUS.results.find((elem) => elem.id == this.$route.query.status)
    if(status){
      this.filter.status = {id: status.id, name: status.name}
    }
   
    let author = this.GET_APPLICATION_AUTHOR.results.find((elem) => elem.id == this.$route.query.author)
    if(author){
      this.filter.author = {id: author.id, name: author.first_name + author.last_name}
    }
    
    let storage = this.GET_APPLICATION_TYPE.find((elem) => elem.id == this.$route.query.storage)
    if(storage){
      this.filter.storage = {id: storage.id, title: storage.title}
    }



    // this.filter.author = this.$route.query.author ? this.$route.query.author : "";
    // this.filter.status = this.$route.query.status ? this.$route.query.status : "";
    this.filter.search = this.$route.query.search ? this.$route.query.search : "";
    this.dateFilter.start_date = this.$route.query.start_date ? this.$route.query.start_date : "";
    this.dateFilter.end_date = this.$route.query.end_date ? this.$route.query.end_date : "";
    this.filter.full = 1;
    this.filter.page_size = this.$route.query.page_size ? this.$route.query.page_size : 10;
    this.filter.show_archive = this.$route.query.show_archive ? this.$route.query.show_archive : 0;
    this.filter.show_purchase = this.$route.query.show_purchase ? this.$route.query.show_purchase : 0;

    setTimeout(() => {
      this.filter.page = this.$route.query.page ? this.$route.query.page : 1;
    }, 0)
    // ===============  PRIMARY_REQUESTS_SECTION END ===============




    
    this.isLoading = false;
  },

  // async beforeRouteLeave(to, from, next) {
  //   await this.$_loaderStop();
  //   next();
  // },

  methods: {
    ...mapActions("application", [
      "FETCH_APPLICATIONS_MATERIAL",
      "UPDATE_APPLICATION_MATERIAL",
      "UPDATE_PATCH_APPLICATION_MATERIAL",
      "DELETE_APPLICATION_MATERIAL",
      "FETCH_APPLICATION_STATUS",
      "FETCH_APPLICATION_AUTHOR",
      "FETCH_APPLICATION_TYPE",
    ]),








    // ===============  PAGINATION_SECTION START ===============
    async previousAuthor() {
      if (this.GET_APPLICATION_AUTHOR.previous) {
        this.authorPage--;

        await this.FETCH_APPLICATION_AUTHOR({ page: this.authorPage, role_unique_name: "builder" });
      }
    },

    async nextAuthor() {
      if (this.GET_APPLICATION_AUTHOR.next) {
        this.authorPage++;

        await this.FETCH_APPLICATION_AUTHOR({ page: this.authorPage, role_unique_name: "builder" });
      }
    },
    // ===============  PAGINATION_SECTION START ===============











    // ===============  REFRESH_SECTION START ===============
    async refresh() {
      this.isLoading = true;

      let filter = this.filter
      if(this.filter.status || (this.filter.status && this.filter.status.id)){
        filter.status = typeof this.filter.status == "number" ? this.filter.status : this.filter.status.id
      }
      if(this.filter.storage || (this.filter.storage && this.filter.storage.id)){
        filter.storage = typeof this.filter.storage == "number" ? this.filter.storage : this.filter.storage.id
      }
      if(this.filter.show_archive){
        filter.show_archive = 1
      }
      if(this.filter.show_purchase){
        filter.show_purchase = 1
      }

      await this.FETCH_APPLICATIONS_MATERIAL({
        ...filter,
        ...this.dateFilter,
      });
      
      this.$_addParamsToLocation({ ...filter, ...this.dateFilter })

      this.isLoading = false;
    },
    // ===============  REFRESH_SECTION END ===============












    // ===============  SEARCH_SECTION START ===============
    async onFiltered() {
      this.isLoading = true;
      this.filter.page = 1;

      let filter = this.filter
      if(this.filter.status || (this.filter.status && this.filter.status.id)){
        filter.status = typeof this.filter.status == "number" ? this.filter.status : this.filter.status.id
      }
      if(this.filter.storage || (this.filter.storage && this.filter.storage.id)){
        filter.storage = typeof this.filter.storage == "number" ? this.filter.storage : this.filter.storage.id
      }
      if(this.filter.show_archive){
        filter.show_archive = 1
      }
      if(this.filter.show_purchase){
        filter.show_purchase = 1
      }else{
        filter.show_purchase = 0
      }

      await this.FETCH_APPLICATIONS_MATERIAL({
        ...filter,
        ...this.dateFilter,
      });
      
      this.$_addParamsToLocation({ ...filter, ...this.dateFilter })

      this.isLoading = false;
    },
    async onAuthorSearch(value) {
      if (value) {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(async () => {
          await this.FETCH_APPLICATION_AUTHOR({
            mini: true,
            search: value,
            role_unique_name: "builder",
          });
        }, 500);
      }
    },
    async search(text) {
      this.isLoading = true;
      this.filter.page = 1;
      this.filter.search = text;

      let filter = this.filter
      if(this.filter.status || (this.filter.status && this.filter.status.id)){
        filter.status = typeof this.filter.status == "number" ? this.filter.status : this.filter.status.id
      }
      if(this.filter.storage || (this.filter.storage && this.filter.storage.id)){
        filter.storage = typeof this.filter.storage == "number" ? this.filter.storage : this.filter.storage.id
      }
      if(this.filter.show_archive){
        filter.show_archive = 1
      }
      if(this.filter.show_purchase){
        filter.show_purchase = 1
      }

      await this.FETCH_APPLICATIONS_MATERIAL({
        ...filter,
        ...this.dateFilter,
      });
      

      this.$_addParamsToLocation({ ...filter, ...this.dateFilter })

      this.isLoading = false;
    },
    // ===============  SEARCH_SECTION END ===============














    // ===============  DATE_PICKER_SECTION START ===============
    async datePicker(e) {
      this.isLoading = true;
      this.filter.page = 1;

      let filter = this.filter
      if(this.filter.status || (this.filter.status && this.filter.status.id)){
        filter.status = typeof this.filter.status == "number" ? this.filter.status : this.filter.status.id
      }
      if(this.filter.storage || (this.filter.storage && this.filter.storage.id)){
        filter.storage = typeof this.filter.storage == "number" ? this.filter.storage : this.filter.storage.id
      }
      if(this.filter.show_archive){
        filter.show_archive = 1
      }
      if(this.filter.show_purchase){
        filter.show_purchase = 1
      }

      if (e) {
        this.dateFilter.start_date = e.start;
        this.dateFilter.end_date = e.end;
        await this.FETCH_APPLICATIONS_MATERIAL({
          ...filter,
          ...this.dateFilter,
        });
      } else {
        await this.FETCH_APPLICATIONS_MATERIAL({ ...filter });
      }

      this.$_addParamsToLocation({ ...filter, ...this.dateFilter })

      this.isLoading = false;
    },
    // ===============  DATE_PICKER_SECTION END ===============



















    // ===============  ON_CHANGE_STATUS_SECTION START ===============
    onChangeStatus(props, item) {
      const status = {
        in_consideration: this.$t("submit_this_application_for_review"),
        confirmed: this.$t("confirm_this_application"),
        denied: this.$t("refuse_this_application"),
        closed: this.$t("close_this_application"),
      };
      this.$bvModal
        .msgBoxConfirm(
          this.$t("do_you_really_want") + ` ${status[item.unique_name]} ?`,
          {
            title: this.$i18n.t("attention"),
            size: "sm",
            okVariant: item.color,
            okTitle: this.$t("yes"),
            cancelTitle: this.$t("no"),
            cancelVariant: "outline-secondary",
            hideHeaderClose: false,
            centered: true,
          }
        )
        .then(async (value) => {
          if (!value) return;
          this.UPDATE_PATCH_APPLICATION_MATERIAL({
            status_unique: item.unique_name,
            id: props.item.id,
          }).then(() => {
            this.FETCH_APPLICATIONS_MATERIAL(this.filter);
          });
        });
    },
    // ===============  ON_CHANGE_STATUS_SECTION END ===============

















    // ===============  DELETE_SECTION START ===============
    deleteMaterial(props) {
      this.$_showAreYouSureModal().then((val) => {
        if (val) {
          this.DELETE_APPLICATION_MATERIAL(props.item.id)
            .then(() => {
              this.FETCH_APPLICATIONS_MATERIAL(this.filter);
              this.$_okToast();
            })
            .catch((err) => {
              this.$_errToast(err);
            });
        }
      });
    },
    // ===============  DELETE_SECTION END ===============















    // ===============  CHECK_AND_DISABLE_SECTION START ===============
    checkAndHide(item) {
      let status = true;
      // if(item.unique_name = 'denied'){
      //   status = false
      // }else if(item.unique_name = 'purchase'){
      //   status = false
      // }

      return status;
    },
    checkAndDisableStatusIfNeeded(item) {
      if (item.unique_name == "closed") {
        let status = false;
        this.GET_APPLICATIONS_MATERIAL.results.forEach((elem) => {
          elem.goods.forEach((elem2) => {
            if (elem2.count > 0) {
              status = true;
            }
          });
        });

        return status;
      } else if (item.unique_name == "in_consideration_on_main") {
        let status = true;

        this.GET_APPLICATIONS_MATERIAL.results.forEach((elem) => {
          elem.goods.forEach((elem2) => {
            if (elem2.remainder < elem2.count) {
              status = false;
            }
          });
        });

        return status;
      } else if (item.unique_name == "denied") {
        // this.GET_APPLICATIONS_MATERIAL.results.forEach((elem) => {
        //   console.log(elem)
        //   // elem.goods.forEach((elem2) => {
        //   //   if (elem2.remainder < elem2.count) {
        //   //     return false;
        //   //   }
        //   // })
        // })
        // if (
        //   this.GET_APPLICATIONS_MATERIAL_ID.write_off.length == 0 &&
        //   this.GET_APPLICATIONS_MATERIAL_ID.status.unique_name ==
        //     "in_consideration"
        // ) {
        //   return false;
        // } else {
        //   return true;
        // }
      } else if (item.unique_name == "purchase") {
        // this.GET_APPLICATIONS_MATERIAL.results.forEach((elem) => {
        //   console.log(elem)
        //   // elem.goods.forEach((elem2) => {
        //   //   if (elem2.remainder < elem2.count) {
        //   //     return false;
        //   //   }
        //   // })
        // })
        // if (
        //   this.GET_APPLICATIONS_MATERIAL_ID.movement.length != 0
        //   // && this.checkMovements()
        // ) {
        //   //true true
        //   return true;
        // } else {
        //   return false;
        // }
      }
    },
    // ===============  CHECK_AND_DISABLE_SECTION END ===============












  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#user-table {
  border-collapse: separate;
  border-spacing: 0 1rem;
}

.dark-layout #user-table .tableRowClass {
  background: #19213a;
}

.dark-layout .dark {
  background-color: #283046 !important;
}

#user-table .tableRowClass {
  background: white;
  transform: translateY(5px);
  transition: all 0.5s ease-in-out;
  text-align: center;
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05);

  td {
    border-top: 0;
    padding: 15px !important;

    &:first-child {
      border-top-left-radius: 0.5rem;
      border-bottom-left-radius: 0.5rem;
    }

    &:last-child {
      border-top-right-radius: 0.5rem;
      border-bottom-right-radius: 0.5rem;
    }
  }
}

#user-table .tableRowClass:hover {
  transform: translateY(0);
}

#user-table .tableHeadClass {
  background: none !important;
  border: none !important;
}

#user-table .tableRowClass:hover {
  .tableTitle {
    color: #7367f0 !important;
  }
}

.tableHeadClass {
  text-align: center;
  padding-left: 0 !important;
}

.pagination {
  display: flex;
  margin: 0.25rem 0.25rem 0;
}
.pagination button {
  flex-grow: 1;
  border: 1px solid #ccc;
}
.pagination button:hover {
  cursor: pointer;
}
</style>
